// Showing where a new task went. Once it has arrived in the store: if it is on
// screen, it plays the sheen; if not, a toast names where it went with Show,
// which switches the tab, week, filters and folded days or groups that hide it,
// scrolls to it and plays the sheen there.

import { addDays, fmtDate, isoWeekday, startOfWeek, today } from '@/logic/dates'
import { dayHeading } from '@/logic/labels'
import { filterShowing } from '@/logic/filters'
import { placeDay, routineWeek } from '@/logic/status'
import { useAuth } from '@/store/auth'
import { usePrefs, type TrayKey } from '@/store/prefs'
import { useTasks } from '@/store/tasks'
import { highlightTask, setCalendarDate, setPlanWeek, showToastAction, useUi } from '@/store/ui'
import type { DateStr, Task } from '@/types'

type Navigate = (to: string) => void

interface Target {
  /** The day it sits on in the Plan tab, or the tray group that holds it. */
  day: DateStr | null
  group: TrayKey | null
  label: string
}

function targetOf(t: Task, now: DateStr): Target {
  const day = placeDay(t, now)
  if (day && day >= now) return { day, group: null, label: dayHeading(day, now) }
  if (day) return { day: null, group: 'notdone', label: 'Not done' }
  if (t.kind === 'routine') {
    const r = routineWeek(t, now)
    return r.doneThisWeek ? { day: null, group: 'routines', label: 'next week' } : { day: null, group: 'routines', label: 'Routines' }
  }
  return { day: null, group: 'nodate', label: 'No date' }
}

function elementOf(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(id)}"]`)
}

/** On screen: inside the window and inside every scrolling box around it, such as a day column. */
function inView(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect()
  if (r.height === 0 || r.bottom <= 0 || r.top >= window.innerHeight || r.right <= 0 || r.left >= window.innerWidth) return false
  for (let box = el.parentElement; box && box !== document.body; box = box.parentElement) {
    const style = getComputedStyle(box)
    if (!/(auto|scroll|hidden)/.test(style.overflowX + style.overflowY)) continue
    const b = box.getBoundingClientRect()
    if (r.bottom <= b.top || r.top >= b.bottom || r.right <= b.left || r.left >= b.right) return false
  }
  return true
}

/** Waits for a condition across animation frames, giving up after `ms`. */
function waitFor<T>(read: () => T | null, ms: number): Promise<T | null> {
  const until = Date.now() + ms
  return new Promise((resolve) => {
    const step = () => {
      const v = read()
      if (v) return resolve(v)
      if (Date.now() > until) return resolve(null)
      requestAnimationFrame(step)
    }
    step()
  })
}

/** Brings a task into view by any means: tab, week, filters, folded days and groups, then the sheen. */
export async function goToTask(id: string, navigate: Navigate): Promise<void> {
  const task = useTasks.getState().byId[id]
  if (!task) return
  const now = today()
  const target = targetOf(task, now)
  const uid = useAuth.getState().user?.uid ?? ''
  const prefs = usePrefs.getState()
  prefs.setFilter(filterShowing(task, prefs.filter, uid))
  const onCalendar = window.location.pathname.startsWith('/calendar')
  if (onCalendar && target.day) {
    setCalendarDate(target.day)
    useUi.setState({ calendarMode: 'day', dayTasksOpen: true })
  } else {
    if (!window.location.pathname.startsWith('/plan')) navigate('/plan')
    const wide = window.matchMedia('(min-width: 900px)').matches
    const columnsPhone = !wide && prefs.layoutPhone === 'columns'
    if (target.day) {
      setPlanWeek(startOfWeek(target.day))
      prefs.openWeekday(isoWeekday(target.day))
    } else {
      const routine = task.kind === 'routine' ? routineWeek(task, now) : null
      setPlanWeek(routine?.doneThisWeek ? addDays(startOfWeek(now), 7) : startOfWeek(now))
      prefs.openGroup(target.group!, columnsPhone)
      if (wide) prefs.setTrayColumnOpen(true)
    }
  }
  const el = await waitFor(() => elementOf(id), 1500)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  highlightTask(id)
}

/** Resolves once a just-written task has reached the store (the local write lands at once), or after 2s. */
export async function waitForTask(id: string): Promise<void> {
  await waitFor(() => useTasks.getState().byId[id] ?? null, 2000)
}

/**
 * After a task is added: the sheen where it can be seen, otherwise a toast
 * with Show. Waits for the local write to reach the store first.
 */
export async function revealAdded(id: string, navigate: Navigate): Promise<void> {
  const task = await waitFor(() => useTasks.getState().byId[id] ?? null, 2000)
  if (!task) return
  const el = await waitFor(() => {
    const e = elementOf(id)
    return e && inView(e) ? e : null
  }, 400)
  if (el) {
    highlightTask(id)
    return
  }
  const target = targetOf(task, today())
  const where = target.day ? (target.day === today() ? 'today' : target.label.startsWith('Tomorrow') ? 'tomorrow' : fmtDate(target.day)) : target.label
  showToastAction(`Added to ${where}`, { label: 'Show', run: () => goToTask(id, navigate) })
}
